import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SparklesIcon, GlassWaterIcon, UsersIcon, BikeIcon, TreesIcon, MountainSnowIcon } from 'lucide-react';
const inclusions = ['Welcome drink on arrival', 'Soft bathrobe & slippers', 'Access to sunbeds & relaxation areas', 'Tea, water & coffee throughout your stay', 'Sauna, jacuzzi & hot tub'];
const bikeHighlights = ['Guided e-bike tour through Transylvanian hills', 'Villages like Viscri & Sighișoara', 'Pace tailored to your group', 'Sauna, hot tub & full spa access', 'A day that energizes and calms'];
export function PrivateEvents() {
  const eventsRef = useRef(null);
  const eventsInView = useInView(eventsRef, {
    once: true,
    margin: '-80px'
  });
  const bikeRef = useRef(null);
  const bikeInView = useInView(bikeRef, {
    once: true,
    margin: '-80px'
  });
  return <section id="experience" className="w-full bg-cream py-24 md:py-32 px-6 lg:px-8" data-id="element-249">
      <div className="max-w-6xl mx-auto" data-id="element-250">
        {/* Section header */}
        <motion.div className="text-center mb-20 md:mb-24" initial={{
        opacity: 0,
        y: 30
      }} animate={eventsInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7
      }} data-id="element-251">
          <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage mb-4" data-id="element-252">
            Beyond the Spa
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark mb-6" data-id="element-253">
            Experiences
          </h2>
          <p className="font-body text-bark-light text-base md:text-lg max-w-xl mx-auto leading-relaxed" data-id="element-254">
            Celebrate, explore, and reconnect — in ways that go beyond a single
            treatment.
          </p>
        </motion.div>

        {/* ═══════════════════════════════════════════
            PRIVATE EVENTS
            ═══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" ref={eventsRef} data-id="element-255">
          {/* Left — Text */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={eventsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} data-id="element-256">
            <div className="flex items-center gap-3 mb-6" data-id="element-257">
              <div className="w-12 h-12 rounded-xl bg-terracotta/10 flex items-center justify-center" data-id="element-258">
                <GlassWaterIcon className="w-6 h-6 text-terracotta" data-id="element-259" />
              </div>
              <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-terracotta/70" data-id="element-260">
                Private Events
              </p>
            </div>

            <h3 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark leading-tight mb-6" data-id="element-261">
              Celebrate
              <br data-id="element-262" />
              Together
            </h3>

            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-5" data-id="element-263">
              We host intimate private gatherings for up to 15 guests — the
              perfect setting for birthdays, bachelorette celebrations,
              anniversaries, or simply a beautiful day with the people you love.
            </p>

            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-5" data-id="element-264">
              Warm sauna rituals, soothing hot tubs under the open sky, relaxing
              massages, and the peaceful energy of nature all around you. A
              cozy, exclusive space where you can celebrate, reconnect, and
              truly unwind together.
            </p>

            <p className="font-heading text-xl italic text-bark/70 mb-10" data-id="element-265">
              Private. Personal. Memorable.
            </p>

            <div className="flex flex-wrap gap-3" data-id="element-266">
              <a href="https://wa.me/40771047817?text=Hi!%20I'd%20like%20to%20book%20a%20private%20event%20at%20Zen%20Barn%20Spa." target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white font-body font-medium text-sm tracking-wide rounded-full hover:bg-terracotta-dark transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-cream" data-id="element-267">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-268">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-269" />
                </svg>
                Message Us to Book
              </a>
              <div className="flex items-center gap-2 px-5 py-4" data-id="element-270">
                <UsersIcon className="w-4 h-4 text-bark-light" data-id="element-271" />
                <span className="font-body text-sm text-bark-light" data-id="element-272">
                  Up to 15 guests
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right — Inclusions card */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={eventsInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} data-id="element-273">
            <div className="relative bg-warm-white rounded-2xl p-10 md:p-12 overflow-hidden" data-id="element-274">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-terracotta/0 via-terracotta/30 to-terracotta/0" data-id="element-275" />
              <div className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full bg-terracotta/5 blur-3xl pointer-events-none" data-id="element-276" />

              <div className="relative" data-id="element-277">
                <p className="text-sm font-body font-medium tracking-[0.2em] uppercase text-terracotta/70 mb-2" data-id="element-278">
                  Every event includes
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-bark mb-8" data-id="element-279">
                  Your Experience
                </h3>

                <ul className="space-y-5" data-id="element-280">
                  {inclusions.map((item, i) => <motion.li key={item} initial={{
                  opacity: 0,
                  x: -15
                }} animate={eventsInView ? {
                  opacity: 1,
                  x: 0
                } : {}} transition={{
                  duration: 0.5,
                  delay: 0.4 + i * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} className="flex items-start gap-4" data-id="element-281">
                      <div className="w-8 h-8 rounded-lg bg-terracotta/8 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-282">
                        <SparklesIcon className="w-4 h-4 text-terracotta" data-id="element-283" />
                      </div>
                      <span className="font-body text-bark text-base md:text-lg leading-snug" data-id="element-284">
                        {item}
                      </span>
                    </motion.li>)}
                </ul>

                <div className="mt-10 pt-8 border-t border-stone/50" data-id="element-285">
                  <p className="font-body text-xs text-bark-light/60 uppercase tracking-wider mb-3" data-id="element-286">
                    Perfect for
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-287">
                    {['Birthdays', 'Bachelorettes', 'Anniversaries', 'Gatherings'].map(tag => <span key={tag} className="px-4 py-1.5 rounded-full bg-cream text-sm font-body font-medium text-bark-light" data-id="element-288">
                        {tag}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ═══════════════════════════════════════════
            DIVIDER
            ═══════════════════════════════════════════ */}
        <div className="my-20 md:my-28 flex items-center gap-6" data-id="element-289">
          <div className="flex-1 h-px bg-stone" data-id="element-290" />
          <div className="w-2 h-2 rounded-full bg-sage/40" data-id="element-291" />
          <div className="flex-1 h-px bg-stone" data-id="element-292" />
        </div>

        {/* ═══════════════════════════════════════════
            E-BIKE & SPA EXPERIENCE
            ═══════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center" ref={bikeRef} data-id="element-293">
          {/* Left — Highlights card */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={bikeInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="order-2 lg:order-1" data-id="element-294">
            <div className="relative bg-warm-white rounded-2xl p-10 md:p-12 overflow-hidden" data-id="element-295">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-sage/0 via-sage/40 to-sage/0" data-id="element-296" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-sage/5 blur-3xl pointer-events-none" data-id="element-297" />

              <div className="relative" data-id="element-298">
                <p className="text-sm font-body font-medium tracking-[0.2em] uppercase text-sage/70 mb-2" data-id="element-299">
                  The journey includes
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-bark mb-8" data-id="element-300">
                  Your Adventure
                </h3>

                <ul className="space-y-5" data-id="element-301">
                  {bikeHighlights.map((item, i) => <motion.li key={item} initial={{
                  opacity: 0,
                  x: -15
                }} animate={bikeInView ? {
                  opacity: 1,
                  x: 0
                } : {}} transition={{
                  duration: 0.5,
                  delay: 0.4 + i * 0.1,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }} className="flex items-start gap-4" data-id="element-302">
                      <div className="w-8 h-8 rounded-lg bg-sage/10 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-303">
                        {i < 3 ? <MountainSnowIcon className="w-4 h-4 text-sage" data-id="element-304" /> : <SparklesIcon className="w-4 h-4 text-sage" data-id="element-305" />}
                      </div>
                      <span className="font-body text-bark text-base md:text-lg leading-snug" data-id="element-306">
                        {item}
                      </span>
                    </motion.li>)}
                </ul>

                <div className="mt-10 pt-8 border-t border-stone/50" data-id="element-307">
                  <p className="font-body text-xs text-bark-light/60 uppercase tracking-wider mb-3" data-id="element-308">
                    In partnership with
                  </p>
                  <p className="font-heading text-lg font-semibold text-bark" data-id="element-309">
                    Ride Therapy · Saschiz
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right — Text */}
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={bikeInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} className="order-1 lg:order-2" data-id="element-310">
            <div className="flex items-center gap-3 mb-6" data-id="element-311">
              <div className="w-12 h-12 rounded-xl bg-sage/10 flex items-center justify-center" data-id="element-312">
                <BikeIcon className="w-6 h-6 text-sage" data-id="element-313" />
              </div>
              <p className="text-sm font-body font-medium tracking-[0.25em] uppercase text-sage" data-id="element-314">
                E-Bike & Spa
              </p>
            </div>

            <h3 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold text-bark leading-tight mb-6" data-id="element-315">
              Ride, Breathe,
              <br data-id="element-316" />
              Restore
            </h3>

            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-5" data-id="element-317">
              A package that perfectly blends the energy of nature with deep
              relaxation. Guided e-bike tours by Ride Therapy in Saschiz take
              you through rolling hills, wildflower meadows, and authentic
              villages like Viscri and Sighișoara — at a pace tailored to your
              group.
            </p>

            <p className="font-body text-bark-light text-base md:text-lg leading-relaxed mb-5" data-id="element-318">
              The journey continues in Richiș with indulgent moments in our
              rural spa — the warmth of the sauna, the scent of wood, and the
              quiet of nature transforming movement into total relaxation.
            </p>

            <p className="font-heading text-xl italic text-bark/70 mb-10" data-id="element-319">
              A day that energizes your body and calms your mind.
            </p>

            <div className="flex flex-wrap items-center gap-4" data-id="element-320">
              <a href="https://wa.me/40771047817?text=Hi!%20I'm%20interested%20in%20the%20e-bike%20%26%20spa%20experience." target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-sage text-white font-body font-medium text-sm tracking-wide rounded-full hover:bg-sage/90 transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-cream" data-id="element-321">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" data-id="element-322">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" data-id="element-323" />
                </svg>
                Inquire About This Experience
              </a>
              <span className="font-body text-sm text-bark-light italic" data-id="element-324">
                Upon request
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}